/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon } from 'semantic-ui-react';
import { omit } from 'lodash';

import { Modal, Button } from '../../../../components';
import { ProductForm } from '../form';
import { prepareFloatForServer } from '../../../../lib/numberFormatter';

const UPDATE_PRODUCT_QUERY = gql`
    mutation updateProduct($input: UpdateProductInput!) {
        updateProduct(input: $input) {
            product {
                id
                name
                code
                manufacturer {
                    id
                    name
                }
                price
                supplierPrice
                quantity
                type
            }
        }
    }
`;

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.inventory.form.ProductForm.existingsError',
        defaultMessage: 'This product does not exists',
    },
    nameExistingsError: {
        id: 'pages.inventory.AddProductDialog.nameExistingsError',
        defaultMessage: 'Product with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

function buildInitialValues(product) {
    return {
        name: product.name,
        code: product.code || '',
        manufacturer: product.manufacturer.id,
        quantity: product.quantity,
        price: parseFloat(product.price).toFixed(2),
        supplierPrice: product.supplierPrice == null
            ? ''
            : parseFloat(product.supplierPrice).toFixed(2),
        type: product.type,
    };
}

function EditProduct({ product, onClose, intl }) {
    const salon = useSelector(({ user }) => user.get('salon'));

    const methods = useForm({ defaultValues: buildInitialValues(product) });
    const { handleSubmit, reset, setError, formState: { isSubmitting, isDirty } } = methods;

    useEffect(() => {
        reset(buildInitialValues(product));
    }, [product.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const [updateProduct] = useMutation(UPDATE_PRODUCT_QUERY);

    const onSubmit = async formData => {
        const supplierPrice = formData.supplierPrice === '' ? null : formData.supplierPrice;
        const quantity = parseInt(formData.quantity);
        const price = prepareFloatForServer(formData.price);
        const resolvedSupplierPrice = supplierPrice ? prepareFloatForServer(supplierPrice) : undefined;

        try {
            await updateProduct({
                variables: {
                    input: {
                        productId: product.id,
                        ...omit(formData, ['quantity']),
                        quantity,
                        price,
                        supplierPrice: resolvedSupplierPrice,
                        salon,
                    },
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    updateProduct: {
                        __typename: 'UpdateProductPayload',
                        product: {
                            __typename: 'Product',
                            id: product.id,
                            manufacturer: { __typename: 'Manufacturer', id: formData.manufacturer, name: '' },
                            ...omit(formData, ['manufacturer']),
                        },
                    },
                },
            });
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (graphQLError.name === 'NotExists' && graphQLError.data?.error === 'ID_DOES_NOT_EXISTS') {
                    setError('root', { message: intl.formatMessage(intlMessages.existingsError) });
                } else if (graphQLError.name === 'AlreadyExists' && graphQLError.data?.error === 'NAME_ALREADY_EXISTS') {
                    setError('name', { message: intl.formatMessage(intlMessages.nameExistingsError) });
                } else {
                    setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
                }
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal.Context>
                <Modal.Content>
                    <ProductForm />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.inventory.details.EditProduct.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.inventory.details.EditProduct.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal.Context>
        </FormProvider>
    );
}

export default injectIntl(EditProduct);
